import React, { useState, useEffect, useRef } from 'react';
import { requestApi } from '../services/api';

const RequestTable = ({ requests, onStatusUpdate, onRefresh, onFilterStatus, currentStatusFilter, onSort: propOnSort, initialSortField = 'id', initialSortDirection = 'desc' }) => {
  // State for sorting
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  
  // Get current filters from URL or props
  const getActiveStatusFilter = () => {
    // Try to get from URL first
    const urlParams = new URLSearchParams(window.location.search);
    const urlStatus = urlParams.get('status');
    if (urlStatus) return urlStatus;
    
    // If not in URL, check if any requests have a common status
    if (requests && requests.length > 0) {
      const firstStatus = requests[0].status;
      const allSameStatus = requests.every(req => req.status === firstStatus);
      if (allSameStatus) return firstStatus;
    }
    
    return '';
  };
  
  const [activeStatusFilter, setActiveStatusFilter] = useState(currentStatusFilter || '');
  
  // Update active filter when currentStatusFilter changes
  useEffect(() => {
    setActiveStatusFilter(currentStatusFilter || '');
  }, [currentStatusFilter]);
  
  // Handle sorting
  const onSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    
    // Call parent's onSort if provided
    if (propOnSort) {
      propOnSort(field, sortField === field && sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [statusFilterPosition, setStatusFilterPosition] = useState({ top: 0, left: 0 });
  const [updatingId, setUpdatingId] = useState(null);
  const [showStatusOptions, setShowStatusOptions] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const statusBadgeRefs = useRef({});
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside any status badge or options
      if (showStatusOptions !== null && 
          !event.target.closest('.status-badge') && 
          !event.target.closest('.status-options')) {
        setShowStatusOptions(null);
      }
      
      // Check if the click is outside the status filter
      if (showStatusFilter && 
          !event.target.closest('.status-column') && 
          !event.target.closest('.status-filter-options')) {
        setShowStatusFilter(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusOptions, showStatusFilter]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setUpdatingId(id);
      await requestApi.updateStatus(id, newStatus);
      onStatusUpdate(id, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-400';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'FINISHED':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400';
      case 'ERROR':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusDot = (status) => {
    const dotClass = (() => {
      switch (status) {
        case 'PENDING':
          return 'bg-warning-500';
        case 'PROCESSING':
          return 'bg-blue-500';
        case 'FINISHED':
          return 'bg-success-500';
        case 'ERROR':
          return 'bg-error-500';
        default:
          return 'bg-gray-500';
      }
    })();
    return <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // State to track which JSON data popup is shown
  const [jsonPopupData, setJsonPopupData] = useState(null);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [jsonPopupPosition, setJsonPopupPosition] = useState({ top: 0, left: 0 });
  const [jsonPopupType, setJsonPopupType] = useState('Body'); // 'Body' or 'Response'

  // Function to show JSON popup
  const showJsonPopup = (data, event, type = 'Body') => {
    if (!data) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.left;
    
    // Position the popup based on available space
    let top, left;
    
    if (spaceBelow < 300 && rect.top > 300) {
      // Position above if not enough space below but enough space above
      top = rect.top - 320;
    } else {
      // Position below otherwise
      top = rect.bottom + 10;
    }
    
    if (spaceRight < 400) {
      // Align right edge if not enough space to the right
      left = window.innerWidth - 420;
    } else {
      // Align left edge otherwise
      left = rect.left;
    }
    
    setJsonPopupPosition({ top, left });
    setJsonPopupData(data);
    setJsonPopupType(type);
  };

  // Function to close JSON popup
  const closeJsonPopup = () => {
    setJsonPopupData(null);
    setJsonCopied(false);
  };

  // Selecting the text by hand works again now that the stray listener is
  // gone, but copying is the whole reason this popup gets opened, so make it
  // one click. execCommand is the fallback for pages not served over HTTPS,
  // where navigator.clipboard is undefined.
  const copyJsonPopup = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(jsonPopupData);
      } else {
        const area = document.createElement('textarea');
        area.value = jsonPopupData;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 1500);
    } catch (err) {
      console.error('Could not copy to the clipboard:', err);
    }
  };

  // Closing on an outside click is handled by the overlay's onClick below,
  // with stopPropagation on the panel. A document-level listener used to do it
  // as well, but it looked for a `.json-popup` class that no element carries,
  // so every press counted as "outside" — and because it listened for
  // mousedown rather than click, the popup vanished the instant you pressed
  // to start selecting the JSON. There was no way to copy a value out.
  //
  // Escape still closes it; that is the part the listener was actually adding.
  useEffect(() => {
    if (!jsonPopupData) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeJsonPopup();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [jsonPopupData]);

  // Function to format JSON data with preview
  const formatJsonData = (data, id) => {
    if (!data) return '—';
    
    try {
      // If data is a string that contains JSON, parse it
      const jsonData = typeof data === 'string' ? JSON.parse(data) : data;
      
      // Create a formatted string representation
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // For preview, show only first 30 characters
      const preview = jsonString.length > 30 
        ? jsonString.substring(0, 30) + '...' 
        : jsonString;
      
      return (
        <div className="json-data-container">
          <pre 
            className="json-data preview"
            onClick={(e) => showJsonPopup(jsonString, e)}
          >
            {preview}
          </pre>
        </div>
      );
    } catch (error) {
      console.error('Error formatting JSON data:', error);
      return String(data) || '—';
    }
  };

  // State for decrypt functionality
  const [decryptingId, setDecryptingId] = useState(null);
  const [decryptedData, setDecryptedData] = useState({});

  // State for response functionality
  const [fetchingResponseId, setFetchingResponseId] = useState(null);
  const [responseData, setResponseData] = useState({});

  // Function to handle decrypt request
  const handleDecrypt = async (id) => {
    try {
      setDecryptingId(id);
      const response = await requestApi.decryptRequest(id);
      
      if (response.data) {
        setDecryptedData(prev => ({
          ...prev,
          [id]: response.data
        }));
        
        // Show the decrypted data in a popup
        const jsonString = JSON.stringify(response.data.response.decryptedBody, null, 2);
        setJsonPopupData(jsonString);
        setJsonPopupType('Decrypted Data');
      }
    } catch (error) {
      console.error('Error decrypting request:', error);
      alert('Failed to decrypt request data. Please try again.');
    } finally {
      setDecryptingId(null);
    }
  };

  // Function to handle get response request
  const handleGetResponse = async (id) => {
    try {
      setFetchingResponseId(id);
      const response = await requestApi.getRequestResponse(id);
      
      if (response.data) {
        setResponseData(prev => ({
          ...prev,
          [id]: response.data
        }));
        
        // Show the response data in a popup
        const jsonString = JSON.stringify(response.data.response.response, null, 2);
        setJsonPopupData(jsonString);
        setJsonPopupType('Response Data');
      }
    } catch (error) {
      console.error('Error fetching response:', error);
      alert('Failed to fetch response data. Please try again.');
    } finally {
      setFetchingResponseId(null);
    }
  };

  // Function to format body data with decrypt button
  const formatBodyData = (data, id) => {
    // Always show the decrypt button, even if there's no body data
    const hasData = data && data !== null && data !== undefined;
    
    if (!hasData) {
      // No data case - show only decrypt button centered
      return (
        <div className="flex items-center justify-center">
          <button 
            className="inline-flex items-center justify-center w-8 h-8 text-xs font-medium text-white bg-orange-500 rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleDecrypt(id)}
            disabled={decryptingId === id}
            title="Decrypt data"
          >
            {decryptingId === id ? (
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              '!'
            )}
          </button>
        </div>
      );
    }
    
    try {
      // If data is a string that contains JSON, parse it
      const jsonData = typeof data === 'string' ? JSON.parse(data) : data;
      
      // Create a formatted string representation
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // For preview, show only first 30 characters
      const preview = jsonString.length > 30 
        ? jsonString.substring(0, 30) + '...' 
        : jsonString;
      
      return (
        <div className="flex items-center gap-2">
          <pre 
            className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-brand-600 dark:hover:text-brand-400 font-mono bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded"
            onClick={(e) => showJsonPopup(jsonString, e, 'Body')}
          >
            {preview}
          </pre>
          <button 
            className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-orange-500 rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleDecrypt(id)}
            disabled={decryptingId === id}
            title="Decrypt data"
          >
            {decryptingId === id ? (
              <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              '!'
            )}
          </button>
        </div>
      );
    } catch (error) {
      console.error('Error formatting JSON data:', error);
      return (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">{String(data)}</span>
          <button 
            className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-orange-500 rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleDecrypt(id)}
            disabled={decryptingId === id}
            title="Decrypt data"
          >
            {decryptingId === id ? (
              <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              '!'
            )}
          </button>
        </div>
      );
    }
  };

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-theme-sm">
      {jsonPopupData && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={closeJsonPopup}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-lg shadow-theme-xl max-w-4xl max-h-[80vh] w-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{jsonPopupType}</h3>
              <div className="flex items-center gap-3">
              <button
                onClick={copyJsonPopup}
                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {jsonCopied ? 'Copied' : 'Copy'}
              </button>
              <button 
                onClick={closeJsonPopup}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                {jsonPopupData}
              </pre>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => onSort('id')}
              >
                <div className="flex items-center gap-2">
                  <span>ID</span>
                  <span className="text-gray-400">
                    {sortField === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Session ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Ran on
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Took
              </th>
              <th 
                className="status-column px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 relative"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setStatusFilterPosition({
                    top: rect.bottom + 8,
                    left: rect.left
                  });
                  setShowStatusFilter(!showStatusFilter);
                }}
              >
                <div className="flex items-center gap-2">
                  <span>Status</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                {showStatusFilter && (
                  <div 
                    className="status-filter-options absolute z-50 mt-2 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-theme-lg border border-gray-200 dark:border-gray-800 py-1"
                    style={{ 
                      top: `${statusFilterPosition.top}px`, 
                      left: `${statusFilterPosition.left}px` 
                    }}
                  >
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFilterStatus('');
                        setActiveStatusFilter('');
                        setShowStatusFilter(false);
                      }}
                    >
                      All Statuses
                    </button>
                    {['PENDING', 'PROCESSING', 'FINISHED', 'ERROR'].map((status) => (
                      <button
                        key={status}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onFilterStatus(status);
                          setActiveStatusFilter(status);
                          setShowStatusFilter(false);
                        }}
                      >
                        {getStatusDot(status)} {status.toLowerCase()}
                      </button>
                    ))}
                  </div>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Response
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Body
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => onSort('createdAt')}
              >
                <div className="flex items-center gap-2">
                  <span>Created</span>
                  <span className="text-gray-400">
                    {sortField === 'createdAt' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
            {requests.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No requests found</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    No requests match your current filters.
                  </p>
                </td>
              </tr>
            ) : (
              requests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    #{request.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {request.sessionId || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {request.category || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {request.botServerName || (request.botServerId ? `#${request.botServerId}` : "—")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {/* Time the BOT spent, not the time spent queued behind the same
                        POS account — those are very different numbers. */}
                    {typeof request.runSeconds === "number"
                      ? (request.runSeconds >= 60
                          ? `${Math.floor(request.runSeconds / 60)}m ${request.runSeconds % 60}s`
                          : `${request.runSeconds}s`)
                      : "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm relative">
                    <div className="flex items-center">
                      <span 
                        className={`status-badge inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getStatusClass(request.status)}`}
                        ref={el => statusBadgeRefs.current[request.id] = el}
                        onClick={(e) => {
                          if (showStatusOptions === request.id) {
                            setShowStatusOptions(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const spaceAbove = rect.top;
                            
                            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                              setDropdownPosition({
                                top: rect.top - 180,
                                left: rect.left
                              });
                            } else {
                              setDropdownPosition({
                                top: rect.bottom + 8,
                                left: rect.left
                              });
                            }
                            setShowStatusOptions(request.id);
                          }
                        }}
                      >
                        {getStatusDot(request.status)}
                        {request.status.toLowerCase()}
                      </span>
                      {showStatusOptions === request.id && (
                        <div 
                          className="status-options fixed z-50 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-theme-lg border border-gray-200 dark:border-gray-800 py-1"
                          style={{ 
                            top: `${dropdownPosition.top}px`, 
                            left: `${dropdownPosition.left}px` 
                          }}
                        >
                          {['PENDING', 'PROCESSING', 'FINISHED', 'ERROR'].map((status) => (
                            <button
                              key={status}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                              onClick={() => {
                                handleStatusChange(request.id, status);
                                setShowStatusOptions(null);
                              }}
                            >
                              {getStatusDot(status)} {status.toLowerCase()}
                            </button>
                          ))}
                        </div>
                      )}
                      {updatingId === request.id && (
                        <span className="ml-2 text-xs text-gray-500 animate-pulse">Updating...</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button 
                      className="inline-flex items-center justify-center w-8 h-8 text-xs font-medium text-white bg-brand-500 rounded-full hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleGetResponse(request.id)}
                      disabled={fetchingResponseId === request.id}
                      title="Get response data"
                    >
                      {fetchingResponseId === request.id ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        'R'
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{formatBodyData(request.body, request.id)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(request.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RequestTable;
